import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
  Button,
  Container,
  Divider,
  Form,
  Grid,
  Message,
  Table,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import MyTextInput from "../../app/common/form/MyTextInput";
import PageHeader from "../../app/layout/PageHeader";
import { useStore } from "../../app/stores/store";
import LoadingComponent from "../../app/layout/LoadingComponent";
import MyPaging from "../../app/common/form/MyPaging";
import { PagingParams } from "../../app/models/pagination";
import PartDetailsSearch from "./PartDetailsSearch";
import { TicketItemType } from "../../app/models/enums";
import PartItemForm from "../tickets/dashboard/modals/PartItemForm";
import { formatCurrency } from "../../app/common/util/functions";
import {
  InventoryIndexDto,
  PartInventoryFormValues,
  SearchInventoryIndexDto,
} from "../../app/models/inventory";
import MyDropdownInput from "../../app/common/form/MyDropdownInput";
import InventoryStockLocationSelect from "../../app/common/form/InventoryStockLocationSelect";

export default observer(function InventoryIndex() {
  const { t } = useTranslation(["common", "inventory"]);
  const navigate = useNavigate();
  const {
    vendorStore,
    modalStore,
    inventoryIndexStore,
  } = useStore();
  const { loadDropdownListVendors, vendorDropdownList } = vendorStore;
  const {
    loadingInitial,
    inventoryIndexPagination,
    setInventoryIndexPredicate,
    hasInventoryIndexPredicate,
    resetInventoryIndexPredicate,
    loadDropdownListStockLocations,
    loadInventoryIndex,
    getInventoryIndex,
    setInventoryIndexPagingParams,
    inventoryIndexPagingParams,
    resetInventoryIndexRegistry,
    setExpandedInventoryIndexItem,
    setExpandedInventoryIndexItemVendor,
    expandedInventoryIndexItemVendor,
    setExpandedTicketsPredicate,
    addPartToStock,
    updatePartInStock,
    setNavigatedToInventoryIndexItem,
    removePart,
    setInventoryStockLocationList,
    inventoryStockLocationList,
  } = inventoryIndexStore;
  
  const [expand, setExpand] = useState<boolean>(false);

  const handleExpand = (inventoryItem: InventoryIndexDto) => (e: any) => {
    if (
      expand &&
      expandedInventoryIndexItemVendor &&
      inventoryItem.inventoryStockId ===
        expandedInventoryIndexItemVendor?.inventoryIndexId
    )
      setExpand(false);
    else setExpand(true);

    setExpandedInventoryIndexItem(inventoryItem);
    setExpandedInventoryIndexItemVendor({
      inventoryIndexId: inventoryItem.inventoryStockId,
      vendorName: inventoryItem.vendorName ?? "",
      vendorId: inventoryItem.vendorId ?? "",
      markup: inventoryItem.markup ?? 0,
      quantityInStock: inventoryItem.quantityInStock ?? 0,
      cost: inventoryItem.cost ?? 0,
      coreTracking: inventoryItem.core ?? false,
    });
    setExpandedTicketsPredicate("vendorId", inventoryItem.vendorId);
  };

  let initFilterValues: SearchInventoryIndexDto = {
    partNumber: undefined,
    partCode: undefined,
    locationId: undefined,
    vendorId: undefined,
    sortKey: undefined,
    order: undefined,
  };
  const [isSearched, setIsSearched] = useState<boolean>(false);

  async function handleFormSubmit(values: SearchInventoryIndexDto) {
    Object.keys(values).map((vk: string) => {
      const valuesAsAny = values as any;
      setIsSearched(true);
      return setInventoryIndexPredicate(vk, valuesAsAny[vk]);
    });
    await loadInventoryIndex();
  }

  useEffect(() => {
    loadDropdownListVendors();
  }, [loadDropdownListVendors]);
  useEffect(() => {
    loadDropdownListStockLocations();
  }, [loadDropdownListStockLocations]);
  useEffect(() => {
    loadInventoryIndex();
  }, [loadInventoryIndex, removePart]);

  function listOnClick(value: number) {
    if (
      value > 0 &&
      inventoryIndexPagination &&
      value < inventoryIndexPagination?.totalPages + 1
    ) {
      setInventoryIndexPagingParams(new PagingParams(value, 25));
      resetInventoryIndexRegistry();
      loadInventoryIndex();
    }
  }

  if (loadingInitial && !isSearched)
    return (
      <LoadingComponent
        content={t("inventoryIndex_loading", { ns: "inventory" })}
      />
    );

  return (
    <>
      <Grid stackable columns={2}>
        <Grid.Column>
          <PageHeader
            header={t("inventory", { ns: "common" })}
            type="h1"
            divider={false}
            getAlerts={true}
            addTitle={true}
            showHelp
            seeMore={true}
            helpName="index.inventoryIndex"
          />
        </Grid.Column>
        <Grid.Column textAlign="right">
          <Button
            color="green"
            icon="plus"
            content="Add New Part"
            onClick={() => {
              modalStore.openModal(
                <PartItemForm
                  isAdd={true}
                  excludeInventoryParts={true}
                  addItem={async (
                    values: PartInventoryFormValues,
                    type: TicketItemType
                  ) => {
                    await addPartToStock({
                      partId: values.partId ?? undefined,
                      partNumber: values.partCode ?? "",
                      description: values.description ?? "",
                      location: values.location,
                      locationId: values.locationId,
                      vendorId: values.vendorId,
                      cost: values.unitPrice ?? 0,
                      price: values.rate ?? 0,
                      quantity: values.quantity,
                      reOrderLevel: values.reOrderLevel ?? 0,
                      markup: values.partMarkup ?? 0,
                      notes: values.notes,
                      lowInventoryReminderEnabled:
                        values.lowInventoryReminderEnabled ?? false,
                      coreTrackingEnabled: values.coreTrackingEnabled ?? false,
                      coreTrackingId: undefined,
                      coreValue: values.coreValue,
                    });
                    resetInventoryIndexPredicate();
                    await loadInventoryIndex();
                  }}
                  autoCapitalizeFirstLetter={false}
                  vendorDropdownList={vendorDropdownList}
                  forInventory={true}
                />
              );
            }}
            data-testid="add"
          />
          <Button
            color="green"
            icon="plus"
            content="Add New Purchase"
            onClick={() => {
              navigate(`/inventory/bulkOrder/`);
            }}
          />
        </Grid.Column>
      </Grid>
      <Divider />
      <Container className="page">
        <Formik initialValues={initFilterValues} onSubmit={handleFormSubmit}>
          {({ handleSubmit, values, setFieldValue }) => (
            <Form className="ui form" onSubmit={handleSubmit}>
              <Grid stackable columns={"equal"}>
                <Grid.Row verticalAlign="middle">
                  <Grid.Column width={2}>
                    <MyTextInput
                      placeholder={t("filters.partNumber", {
                        ns: "inventory",
                      })}
                      name="partNumber"
                      label={`${t("labels.partNumber", {
                        ns: "inventory",
                      })}: `}
                      autoCapitalizeFirstLetter={false}
                      maxLength={25}
                    />
                  </Grid.Column>
                  <Grid.Column width={3}>
                    <MyTextInput
                      placeholder={t("filters.description", {
                        ns: "inventory",
                      })}
                      name="description"
                      label={`${t("labels.description", {
                        ns: "inventory",
                      })}: `}
                      autoCapitalizeFirstLetter={false}
                      maxLength={25}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <MyDropdownInput
                        options={vendorDropdownList}
                        label={t("labels.vendorName", { ns: "inventory" }) + ":"}
                        placeholder={t("filters.vendorName", { ns: "inventory" })}
                        name="vendorId"
                      />
                  </Grid.Column>
                  <Grid.Column>
                      <InventoryStockLocationSelect
                        options={inventoryStockLocationList}
                        setOptions={setInventoryStockLocationList}
                        label={`${t("labels.location", {
                          ns: "inventory",
                        })}: `}
                        placeholder={t("filters.location", {
                          ns: "inventory",
                        })}
                        name="locationId"
                        clearable={true}
                        onChange={(val) => {
                          const selectedLoc =
                            inventoryStockLocationList.find(
                              (invStLoc) => invStLoc.value === val
                            );

                          if (selectedLoc) {
                            setFieldValue("location", selectedLoc.text);
                            setFieldValue("locationId", selectedLoc.value);
                          } else {
                            setFieldValue("location", val);
                          }
                        }}
                        hideAdd={true}
                      />
                  </Grid.Column>
                  
                  <Grid.Column className="slightPaddingTop">
                      <Button
                        color="green"
                        className="save_button"
                        icon="search"
                        content={t("filters.search", { ns: "inventory" })}
                        type="submit"
                      />
                      {isSearched && hasInventoryIndexPredicate() && (
                        <Button
                          color="grey"
                          className="save_button"
                          content={t("filters.clearSearch", {
                            ns: "inventory",
                          })}
                          type="button"
                          icon="cancel"
                          onClick={() => {
                            resetInventoryIndexPredicate();
                            setIsSearched(false);
                          }}
                        />
                      )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          )}
        </Formik>
        {loadingInitial && isSearched ? (
          <LoadingComponent
            content={t("inventoryIndex_loading", { ns: "inventory" })}
          />
        ) : (
          <Table striped unstackable className="dnxTable">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell></Table.HeaderCell>
                <Table.HeaderCell>
                  {t("labels.partNumber", { ns: "inventory" })}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t("labels.description", { ns: "inventory" })}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.totalQuantity", { ns: "inventory" })}`}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.avgCost", { ns: "inventory" })}`}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.unitPrice", { ns: "inventory" })}`}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {`${t("labels.location", { ns: "inventory" })}`}
                </Table.HeaderCell>
                <Table.HeaderCell content={" "} />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {getInventoryIndex && getInventoryIndex.length ? (
                getInventoryIndex.map((invIdxItem: InventoryIndexDto) => (
                  <>
                    <Table.Row
                      key={`inventoryIndex.${invIdxItem.inventoryStockId}`}
                    >
                      <Table.Cell>
                        <Button
                          color="grey"
                          icon="edit"
                          type="button"
                          onClick={() => {
                            modalStore.openModal(
                              <PartItemForm
                                isAdd={false}
                                part={{
                                  id: invIdxItem.partId, //Part Id
                                  partCode: invIdxItem.partNumber,
                                  description: invIdxItem.description,
                                  location: invIdxItem.location,
                                  locationId: invIdxItem.locationId,
                                  vendorId: invIdxItem.vendorId,
                                  partMarkup: invIdxItem.markup,
                                  unitPrice: invIdxItem.cost,
                                  rate: invIdxItem.unitPrice,
                                  quantity: invIdxItem.quantityInStock === 0 ? 0 : invIdxItem.quantityInStock ?? 1,
                                  reOrderLevel: invIdxItem.reOrderLevel,
                                  notes: invIdxItem.notes,
                                  coreTrackingEnabled:
                                    invIdxItem.coreTrackingEnabled ?? false,
                                  coreTrackingId: invIdxItem.coreTrackingId,
                                  coreValue: invIdxItem.coreValue,
                                  returned: invIdxItem.returned,
                                  refunded: invIdxItem.refunded,
                                  isTaxable: true,
                                }}
                                addItem={async (
                                  values: PartInventoryFormValues,
                                  type: TicketItemType
                                ) => {
                                  await updatePartInStock({
                                    partId: values.partId ?? invIdxItem.partId,
                                    partNumber: values.partCode ?? "",
                                    description: values.description ?? "",
                                    location: values.location !== "--" ? values.location : undefined,
                                    locationId: values.locationId !== "" ? values.locationId : undefined,
                                    vendorId: values.vendorId,
                                    cost: values.unitPrice ?? 0,
                                    price: values.rate ?? 0,
                                    quantity: values.quantity,
                                    reOrderLevel: values.reOrderLevel ?? 0,
                                    markup: values.partMarkup ?? 0,
                                    lowInventoryReminderEnabled:
                                      values.lowInventoryReminderEnabled ??
                                      false,
                                    coreTrackingEnabled:
                                      values.coreTrackingEnabled ?? false,
                                    coreTrackingId: values.coreTrackingId,
                                    coreValue: values.coreValue,
                                    returned: values.returned,
                                    refunded: values.refunded,
                                    notes: values.notes
                                  });
                                  await loadInventoryIndex();
                                }}
                                autoCapitalizeFirstLetter={false}
                                vendorDropdownList={vendorDropdownList}
                                forInventory={true}
                              />
                            );
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell
                        data-label={`${t("labels.partNumber", {
                          ns: "inventory",
                        })}:`}
                      >
                        {invIdxItem.partNumber}
                      </Table.Cell>
                      <Table.Cell
                        data-label={`${t("labels.description", {
                          ns: "inventory",
                        })}:`}
                      >
                        {invIdxItem.description}
                      </Table.Cell>
                      <Table.Cell
                        data-label={`${t("labels.totalQuantity", {
                          ns: "inventory",
                        })}:`}
                      >
                        {invIdxItem.quantityInStock}
                      </Table.Cell>
                      <Table.Cell
                        data-label={`${t("labels.avgCost", {
                          ns: "inventory",
                        })}:`}
                      >
                        {formatCurrency(invIdxItem.cost ?? 0)}
                      </Table.Cell>
                      <Table.Cell
                        data-label={`${t("labels.unitPrice", {
                          ns: "inventory",
                        })}:`}
                      >
                        {formatCurrency(invIdxItem.unitPrice ?? 0)}
                      </Table.Cell>
                      <Table.Cell
                        data-label={`${t("labels.location", {
                          ns: "inventory",
                        })}:`}
                      >
                        {invIdxItem.location}
                      </Table.Cell>
                      <Table.Cell className="textAlignRight">
                        <Button
                          icon="plus square"
                          size="small"
                          type="button"
                          className="tealishButtons"
                          content={t("labels.seeMore", { ns: "inventory" })}
                          onClick={handleExpand(invIdxItem)}
                        />
                        <Button
                          size="small"
                          content={t("labels.seeAllDetails", {
                            ns: "inventory",
                          })}
                          icon="clipboard list"
                          onClick={() => {
                            setNavigatedToInventoryIndexItem(invIdxItem);
                            navigate(
                              `/inventoryIndex/partDetails/${invIdxItem.partId}`
                            );
                          }}
                        />
                      </Table.Cell>
                    </Table.Row>
                    {expand &&
                      expandedInventoryIndexItemVendor?.inventoryIndexId ===
                        invIdxItem.inventoryStockId && <PartDetailsSearch />}
                  </>
                ))
              ) : (
                <Table.Row key={"noItems"}>
                  <Table.Cell width="8" colSpan={8}>
                    <Message
                      color="yellow"
                      className="textAlignCenter fontStyleItalic"
                    >
                      {t("grid_noItems", { ns: "common" })}
                    </Message>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.Cell colSpan="16">
                  <MyPaging
                    itemCount={
                      inventoryIndexPagination
                        ? inventoryIndexPagination.totalItems
                        : 25
                    }
                    pageSize={inventoryIndexPagingParams.pageSize}
                    currentPage={
                      inventoryIndexPagination
                        ? inventoryIndexPagination.currentPage
                        : 1
                    }
                    onClick={listOnClick}
                    onChange={listOnClick}
                    floated="right"
                  ></MyPaging>
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          </Table>
        )}
      </Container>
    </>
  );
});
